import "./GeneralEditorViewer.scss";
import "@google/model-viewer";

import { useState, useRef } from "react";
import { useCurrentVariant } from "../../contexts/useCurrentVariant";
import { useCurrentProduct } from "../../contexts/useCurrentProduct";
import { useEditorConfig, EditorConfigOverrideProvider } from "../../contexts/useEditorConfig";
import { getVariantUrl } from "../../hooks/getVariantUrl.js";
import { getPropertyOrDefault } from "../../helpers/helpers";

import Hotspot from "../Hotspot/Hotspot";
import ViewerUI from "../ViewerUI/ViewerUI";
import VideoPopupContainer from "../Hotspot/VideoPopup/VideoPopupContainer";
import HotspotModalPopup from "../Hotspot/HotspotModalPopup/HotspotModalPopup";
import { Dimensions } from "../ModelViewer/Dimensions/Dimensions.tsx";
import { useLocalSessionSettings } from "../../contexts/useLocalSessionSettings";
import { useViewerMode } from "../../contexts/useViewerMode";

/**
 * Inner viewer component that uses the overridden config
 */
function ViewerContent({ config }) {
   const [currentVariant] = useCurrentVariant();
   const [currentProduct] = useCurrentProduct();
   const [viewerMode] = useViewerMode();
   const [localSessionSettings] = useLocalSessionSettings();
   
   const modelViewerRef = useRef(null);
   const [currentHotspot, setCurrentHotspot] = useState(null);
   const hotspotsVisible = true;
   const uiVisible = true;

   const showHotspots = viewerMode === "ar" || viewerMode === "explore";
   const androidAndWebModelFormat = currentVariant?.glb || currentVariant?.gltf;
   const iosModelFormat = currentVariant?.usdz;
   const androidAndWebModelSrc = getVariantUrl(currentProduct?.uuid, androidAndWebModelFormat);
   const iosModelSrc = getVariantUrl(currentProduct?.uuid, iosModelFormat);
   
   const enableWebXr = getPropertyOrDefault(config.enableWebXr, false);
   const showInteractionPrompt = getPropertyOrDefault(config.showInteractionPrompt, true);
   const interactionPromptStyle = getPropertyOrDefault(config.interactionPromptStyle, "wiggle");
   const hotspotOptions = getPropertyOrDefault(currentVariant?.hotspot_options, null);
   const toneMapping = getPropertyOrDefault(config.toneMapping, "auto");
   const autoRotate = getPropertyOrDefault(config["auto-rotate"], true);
   const autoRotateSpeed = getPropertyOrDefault(config["auto-rotate-speed"], "100%");
   const disablePan = getPropertyOrDefault(config["disable-pan"], false);
   const dimensionsConfig = getPropertyOrDefault(config.dimensionsConfig, null);

   return (
      <div className="general-editor-viewer">
         {/* Background matching config */}
         <div 
            className="viewer-background"
            style={{
               background: config["background-color"] || "#ffffff",
               backgroundImage: config["background-image"] ? `url(${config["background-image"]})` : 'none',
               backgroundSize: config["background-image-fit"] || "cover",
               backgroundRepeat: config["background-image-repeat"] || "no-repeat",
               backgroundPosition: config["background-image-position"] || "center",
            }}
         />
         
         {/* Custom logos overlay */}
         <div className="custom-logos-overlay">
            {(config.customLogos || []).map((logo, index) => (
               <div 
                  key={logo.id ?? index}
                  className={`custom-logo logo-${logo["custom-logo-position"] || 'left'}`}
                  style={{
                     opacity: logo["custom-logo-opacity"]?.replace('%', '') / 100 || 1,
                  }}
               >
                  {logo["custom-logo"] && (
                     <img 
                        src={logo["custom-logo"]} 
                        alt={`Logo ${index + 1}`}
                        style={{
                           width: logo["custom-logo-width"] || 'auto',
                           maxWidth: '100%',
                        }}
                     />
                  )}
               </div>
            ))}
         </div>

         <model-viewer
            ref={modelViewerRef}
            className="model-viewer"
            alt="Reydar Model Viewer"
            src={androidAndWebModelSrc}
            ios-src={iosModelSrc}
            ar
            ar-modes={enableWebXr ? "webxr scene-viewer quick-look" : "scene-viewer quick-look"}
            ar-scale={config["ar-scale"] ?? "fixed"}
            skybox-image={config["skybox-environment"]}
            environment-image={config["background-environment"]}
            tone-mapping={toneMapping}
            ar-placement={config.arPlacement ?? "floor"}
            loading="auto"
            reveal="auto"
            exposure={config.exposure ?? 1}
            shadow-intensity={config["shadow-intensity"] ?? "1"}
            shadow-softness={config["shadow-softness"] ?? "1"}
            camera-controls
            disable-pan={disablePan ? true : null}
            auto-rotate={autoRotate ? true : null}
            rotation-per-second={autoRotateSpeed}
            interaction-prompt={showInteractionPrompt ? "auto" : "none"}
            interaction-prompt-style={interactionPromptStyle}
            camera-orbit={config["default-camera-orbit"]}
            min-camera-orbit={config["min-camera-orbit"]}
            max-camera-orbit={config["max-camera-orbit"]}
            field-of-view={config["default-fieldOfView"]}
            min-field-of-view={config["min-fieldOfView"]}
            max-field-of-view={config["max-fieldOfView"]}
            camera-target={config.defaultCameraTarget}
            style={{
               "--hotspot-color": config["hotspot-color"] || "#e42026",
            }}
         >
            <button slot="ar-button" style={{ display: "none" }}></button>
            
            {/* Render hotspots */}
            {showHotspots &&
               currentVariant?.hotspots?.map((hotspotData) => (
                  <Hotspot
                     key={hotspotData.id}
                     hotspotData={hotspotData}
                     setCurrentHotspot={setCurrentHotspot}
                     currentHotspot={currentHotspot}
                     hotspotOptions={hotspotOptions}
                     handleAnimationDataChanged={() => {}}
                     hotspotsVisible={hotspotsVisible}
                     arStatus=""
                  />
               ))}
            
            {/* Dimensions if enabled */}
            {dimensionsConfig?.enabled && (
               <Dimensions 
                  modelViewerRef={modelViewerRef} 
                  active={localSessionSettings?.showDimensions} 
               />
            )}
         </model-viewer>

         {/* Viewer UI overlay - this gives the real viewer experience */}
         <ViewerUI uiVisible={uiVisible} />
         
         {/* Video popup container */}
         <VideoPopupContainer />
         
         {/* Hotspot modal */}
         <HotspotModalPopup />

         {/* Editor badge */}
         <div className="editor-badge">
            <span>Live Preview</span>
         </div>
      </div>
   );
}

/**
 * GeneralEditorViewer - Wraps the viewer content with the config override provider
 * so all child components (ViewerUI, Hotspot, etc.) see the live edits
 */
function GeneralEditorViewer() {
   const { config } = useEditorConfig();

   // Only proceed if we have config
   if (!config) {
      return (
         <div className="general-editor-viewer loading">
            <div className="loading-message">Loading viewer...</div>
         </div>
      );
   }

   return (
      <EditorConfigOverrideProvider config={config}>
         <ViewerContent config={config} />
      </EditorConfigOverrideProvider>
   );
}

export default GeneralEditorViewer;

